/**
 * StatusPill, shared status pill (success/warning/danger/info/neutral)
 * with optional dot indicator.
 *
 * Used for dispatch status, OAuth connection status, invitation status, etc.
 * One component, one visual language, no per-page bespoke styling.
 *
 * PLAT-263: `Pill` renders Foundation's Badge (soft, pill-shaped). Foundation
 * Badge has no dot, so the dot stays a decorative aria-hidden span styled by
 * the local `.pill-dot` rule (it inherits the Badge's tone via currentColor).
 */

import { Badge, type BadgeProps } from '@olytic/foundation';
import type { ReactNode } from 'react';

import { formatLabel } from '../lib/format.ts';
import { IconCheck } from './Icon.tsx';

export type PillKind = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

interface PillProps {
  kind: PillKind;
  children: ReactNode;
  dot?: boolean;
}

/** Pill kind → Foundation Badge tone. */
const PILL_TONE = {
  success: 'success',
  warning: 'warning',
  danger: 'error',
  info: 'info',
  neutral: 'neutral',
} as const satisfies Record<PillKind, NonNullable<BadgeProps['tone']>>;

export function Pill({ kind, children, dot = false }: PillProps) {
  return (
    <Badge tone={PILL_TONE[kind]} variant="soft" shape="pill">
      {dot && <span className="pill-dot" aria-hidden="true" />}
      {children}
    </Badge>
  );
}

/** Map dispatch-log status → pill */
export function DispatchStatusPill({ status }: { status: string }) {
  if (status === 'success')
    return (
      <Pill kind="success" dot>
        Success
      </Pill>
    );
  if (status === 'failed')
    return (
      <Pill kind="danger" dot>
        Failed
      </Pill>
    );
  // Push #4.5 Item 9 — admin/activity.ts projects 'denied' for rows
  // where kind='dispatch_denied' (permission_mode='disallow' short-circuit
  // per Migration 0116). Renders as a danger pill so it visually sits
  // alongside transport failures in the workspace observability tab.
  if (status === 'denied')
    return (
      <Pill kind="danger" dot>
        Denied
      </Pill>
    );
  if (status === 'in_flight')
    return (
      <Pill kind="info" dot>
        In flight
      </Pill>
    );
  return <Pill kind="neutral">{formatLabel(status)}</Pill>;
}

/** Map OAuth authorization state → pill */
export function OAuthStatusPill({ status }: { status: string }) {
  if (status === 'connected')
    return (
      <Pill kind="success" dot>
        Connected
      </Pill>
    );
  // Push #82.8 — was kind="warning" (orange/amber). Ean flagged the
  // visual mismatch: the connectors-in-scope rail count badge for
  // needs-reauth is red, but the per-row pill was orange. They both
  // indicate the same severity (reconnect required), so they should
  // share the same color. Switched to `danger` (red) to match the
  // rail badge.
  if (status === 'needs_reauth')
    return (
      <Pill kind="danger" dot>
        Reconnect
      </Pill>
    );
  if (status === 'not_authorized') return <Pill kind="neutral">Not connected</Pill>;
  return <Pill kind="neutral">{formatLabel(status)}</Pill>;
}

/** Map invitation status → pill */
export function InvitationStatusPill({ status }: { status: string }) {
  if (status === 'pending')
    return (
      <Pill kind="info" dot>
        Pending
      </Pill>
    );
  if (status === 'accepted')
    return (
      <Pill kind="success" dot>
        Accepted
      </Pill>
    );
  if (status === 'expired') return <Pill kind="neutral">Expired</Pill>;
  if (status === 'revoked') return <Pill kind="danger">Revoked</Pill>;
  return <Pill kind="neutral">{formatLabel(status)}</Pill>;
}

/** Role tag (small, role-specific tint) */
export function RoleBadge({ role }: { role: 'owner' | 'admin' | 'user' }) {
  return <span className={`role-badge role-badge-${role}`}>{formatLabel(role)}</span>;
}

/**
 * Push #22 canonical state-pill helpers. Every status indicator on a card
 * uses one of these — no bare checkmarks, no floating text. Same pill shape
 * across all surfaces; the kind drives color (teal-tinted for positive/
 * active, gray for passive/future, green-dot for real-time-healthy,
 * red/amber-dot for real-time-degraded).
 *
 * Used by AdminCatalog tiles, Library tiles, PS workshop widgets,
 * connector/prompt/model record headers, and anywhere a card shows
 * a workspace-state badge.
 */

/** "Installed in workspace" — teal-tinted "Installed" with a check glyph. */
export function InstalledPill() {
  // Push #58: swap the Unicode ✓ for the SVG IconCheck per the no-emoji
  // codebase rule (Icon.tsx 2026-06-03). Inline-flex aligns the glyph
  // with the label baseline; aria-hidden because "Installed" already
  // carries the meaning.
  return (
    <Pill kind="info">
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <IconCheck size={11} />
        Installed
      </span>
    </Pill>
  );
}

/** "Available but not installed" — gray "Preview" for state='coming_soon' (DB enum stays; UI label is user-facing). */
export function ComingSoonPill() {
  return <Pill kind="neutral">Preview</Pill>;
}

/** "Assigned to this user / set" — teal-tinted "Assigned". */
export function AssignedPill() {
  return <Pill kind="info">Assigned</Pill>;
}

/** "Distributed to N users" — teal-tinted; falls back to gray when N=0. */
export function DistributionPill({ count }: { count: number }) {
  if (count === 0) return <Pill kind="neutral">Not yet distributed</Pill>;
  return (
    <Pill kind="info">
      Distributed to {count} {count === 1 ? 'user' : 'users'}
    </Pill>
  );
}

/** Custom vs curated source — teal for custom (user-authored), gray for
 *  curated (platform-shipped). */
export function SourcePill({ source }: { source: 'custom' | 'curated' }) {
  if (source === 'custom') return <Pill kind="info">Custom</Pill>;
  return <Pill kind="neutral">Curated</Pill>;
}

/** Auto-maintained marker — used on owner_bootstrap PS rows + read-only
 *  surfaces where the row is platform-managed. */
export function AutoMaintainedPill() {
  return <Pill kind="neutral">Auto-maintained</Pill>;
}
